import { useEffect, useRef, useState } from 'react';
import { getToneAudioEngine } from '../renderer/rendererBridge';
import * as Tone from 'tone';

interface MeterLevels {
  rms: number; // 0-1
  peak: number; // 0-1
}

/**
 * Connect to Tone.js analyser nodes and return real-time RMS/peak levels.
 */
export function useAudioMeter(trackId?: string): MeterLevels {
  const [levels, setLevels] = useState<MeterLevels>({ rms: 0, peak: 0 });
  const rafRef = useRef(0);
  const analyserRef = useRef<Tone.Analyser | null>(null);

  useEffect(() => {
    function tick() {
      const engine = getToneAudioEngine();
      if (!engine) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Lazily connect analyser
      if (!analyserRef.current) {
        analyserRef.current = trackId
          ? engine.getMeterNode(trackId)
          : engine.getMasterMeterNode();
      }

      const analyser = analyserRef.current;
      if (!analyser) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const values = analyser.getValue();
      if (values instanceof Float32Array) {
        let sumSq = 0;
        let peak = 0;
        for (let i = 0; i < values.length; i++) {
          const abs = Math.abs(values[i]);
          sumSq += values[i] * values[i];
          if (abs > peak) peak = abs;
        }
        const rms = Math.sqrt(sumSq / values.length);
        setLevels({ rms: Math.min(1, rms * 3), peak: Math.min(1, peak) });
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      analyserRef.current = null;
    };
  }, [trackId]);

  return levels;
}
